import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { getInstitutionsById } from 'app-components/flux/institutions/institutionsSelectors';
import {
  getAggregatorFromChannel,
  getStaticInstitutionFromAggregator,
} from 'app-components/flux/institutions/institutionsUtils';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import QSegmentedTabs from 'components/QSegmentedTabs';
import SimplifiMultiSelect from 'components/SimplifiSelects/SimplifiMultiSelect';

const GROUPING_MODE = {
  TYPE: 'type',
  INSTITUTION: 'institution',
};

const MANUAL_INSTITUTION_LABEL = 'Manual';

const accountTypeLabelMap = {
  checking: 'Cash & Checking',
  credit: 'credit',
  savings: 'savings',
  brokerage: 'brokerage',
  retirement: 'retirement',
  otherInvestment: 'Other Investment',
  asset: 'asset',
  loan: 'loan',
  other: 'other',
};

const compareByName = (firstValue, secondValue) =>
  (firstValue || '').localeCompare(secondValue || '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const sortAccounts = (accounts) =>
  [...accounts].sort((account1, account2) => compareByName(account1.name, account2.name));

const getTypeGroupedAccounts = (accounts) => {
  const organizedList = {
    checking: [],
    credit: [],
    savings: [],
    brokerage: [],
    retirement: [],
    otherInvestment: [],
    asset: [],
    loan: [],
    other: [],
  };

  sortAccounts(accounts).forEach((account) => {
    switch (account.type) {
      case 'BANK':
      case 'CASH':
        if (account.subType === 'SAVINGS' || account.subType === 'CD' || account.subType === 'MONEY_MARKET') {
          organizedList.savings.push(account);
        } else {
          organizedList.checking.push(account);
        }
        break;
      case 'CREDIT':
        organizedList.credit.push(account);
        break;
      case 'LOAN':
      case 'OTHER_LIABILITY':
        organizedList.loan.push(account);
        break;
      case 'INVESTMENT':
        if (
          account.subType === null ||
          account.subType === '529' ||
          account.subType === 'UGMA_UTMA' ||
          account.subType === 'OTHER_INVESTMENTS'
        ) {
          organizedList.otherInvestment.push(account);
        } else if (account.subType === 'BROKERAGE') {
          organizedList.brokerage.push(account);
        } else {
          organizedList.retirement.push(account);
        }
        break;
      case 'OTHER_ASSET':
      case 'REAL_ESTATE':
      case 'VEHICLE':
        organizedList.asset.push(account);
        break;
      default:
        organizedList.other.push(account);
    }
  });

  return Object.keys(organizedList)
    .filter((key) => organizedList[key].length)
    .map((key) => ({
      name: accountTypeLabelMap[key],
      items: organizedList[key],
    }));
};

const getInstitutionLabel = (account, institutionLoginsById, institutionsById) => {
  const { institutionLoginId } = account;
  const institutionLogin = institutionLoginId ? institutionLoginsById.get(institutionLoginId) : undefined;
  const isStripeAccount =
    institutionLogin?.channel === 'STRIPE' ||
    institutionLogin?.channel === 'STRIPE_TEST' ||
    accountsUtils.isStripeAccount(account);

  if (
    accountsUtils.isAccountManual(account) ||
    !institutionLoginId ||
    (!isStripeAccount &&
      (!institutionLogin || Number(institutionLogin.institutionId) === 0 || !institutionLogin.aggregators?.size))
  ) {
    return MANUAL_INSTITUTION_LABEL;
  }

  if (institutionLogin?.institutionId) {
    const institution = institutionsById.get(institutionLogin.institutionId);
    if (institution?.name) {
      return institution.name;
    }
  }

  if (institutionLogin?.channel) {
    const staticInstitution = getStaticInstitutionFromAggregator(getAggregatorFromChannel(institutionLogin.channel));
    if (staticInstitution?.name) {
      return staticInstitution.name;
    }
  }

  return institutionLogin?.name || account.name || MANUAL_INSTITUTION_LABEL;
};

const getInstitutionGroupedAccounts = (accounts, institutionLoginsById, institutionsById) => {
  const groupedAccounts = sortAccounts(accounts).reduce((groups, account) => {
    const institutionLabel = getInstitutionLabel(account, institutionLoginsById, institutionsById);
    if (!groups[institutionLabel]) {
      groups[institutionLabel] = [];
    }
    groups[institutionLabel].push(account);
    return groups;
  }, {});

  return Object.keys(groupedAccounts)
    .sort((left, right) => {
      if (left === MANUAL_INSTITUTION_LABEL) {
        return 1;
      }
      if (right === MANUAL_INSTITUTION_LABEL) {
        return -1;
      }
      return compareByName(left, right);
    })
    .map((name) => ({
      name,
      items: groupedAccounts[name],
    }));
};

const AccountMultiSelect = (props) => {
  const {
    onSelected,
    name = 'Accounts',
    selectedItems = [],
    showAllItemsMenu = true,
    accountsSelector = accountsSelectors.getTransactionAccounts,
    groupingModes,
    groupingMode,
    onGroupingModeChange,
    ...other
  } = props;

  const accounts = useSelector(accountsSelector);
  const institutionLoginsById = useSelector(institutionLoginsSelectors.getInstitutionLoginsById);
  const institutionsById = useSelector(getInstitutionsById);

  const availableGroupingModes = useMemo(
    () =>
      groupingModes?.length && groupingModes.every((mode) => Object.values(GROUPING_MODE).includes(mode))
        ? groupingModes
        : [GROUPING_MODE.TYPE],
    [groupingModes],
  );

  const [internalGroupingMode, setInternalGroupingMode] = useState(availableGroupingModes[0]);
  const effectiveGroupingMode = groupingMode || internalGroupingMode;

  useEffect(() => {
    if (!availableGroupingModes.includes(effectiveGroupingMode)) {
      const fallbackMode = availableGroupingModes[0];
      setInternalGroupingMode(fallbackMode);
      onGroupingModeChange?.(fallbackMode);
    }
  }, [availableGroupingModes, effectiveGroupingMode, onGroupingModeChange]);

  const selectedAccounts = useMemo(
    () => selectedItems.map((id) => accounts.get(id)).filter(Boolean),
    [accounts, selectedItems],
  );

  const menuArray = useMemo(() => {
    const accountsList = accounts.toIndexedSeq().toArray();
    if (effectiveGroupingMode === GROUPING_MODE.INSTITUTION) {
      return getInstitutionGroupedAccounts(accountsList, institutionLoginsById, institutionsById);
    }
    return getTypeGroupedAccounts(accountsList);
  }, [accounts, effectiveGroupingMode, institutionLoginsById, institutionsById]);

  const showAll = showAllItemsMenu && menuArray.filter((group) => group.items.length).length > 1;

  const showGroupingSwitch = availableGroupingModes.length > 1;

  const handleGroupingModeChange = (_event, nextGroupingMode) => {
    if (!nextGroupingMode) {
      return;
    }

    if (!groupingMode) {
      setInternalGroupingMode(nextGroupingMode);
    }

    onGroupingModeChange?.(nextGroupingMode);
  };

  const menuTopContent = (
    <Box
      sx={{
        pt: 2,
        px: 2,
        pb: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Typography variant='bodyRegular' sx={{ whiteSpace: 'nowrap' }}>
        Select Accounts
      </Typography>
      {showGroupingSwitch ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Typography variant='captionRegular' sx={{ whiteSpace: 'nowrap' }}>
            Group by
          </Typography>
          <QSegmentedTabs
            size='small'
            value={effectiveGroupingMode}
            onChange={handleGroupingModeChange}
            aria-label='Account grouping mode'
          >
            <ToggleButton value={GROUPING_MODE.TYPE} aria-label='Account Type' sx={{ whiteSpace: 'nowrap' }}>
              Account Type
            </ToggleButton>
            <ToggleButton value={GROUPING_MODE.INSTITUTION} aria-label='Institution' sx={{ whiteSpace: 'nowrap' }}>
              Institution
            </ToggleButton>
          </QSegmentedTabs>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <SimplifiMultiSelect
      sharedcomponentid='ACCOUNT_MULTI_SELECT'
      menuArray={menuArray}
      menuTopContent={menuTopContent}
      menuTopContentSticky
      menuTopContentDivider
      onSelected={onSelected}
      name={name}
      selectedItems={selectedAccounts}
      showAll={showAll}
      useCustomMenuLayout
      {...other}
    />
  );
};

AccountMultiSelect.propTypes = {
  accountsSelector: PropTypes.func,
  className: PropTypes.string,
  groupingMode: PropTypes.oneOf(Object.values(GROUPING_MODE)),
  groupingModes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(GROUPING_MODE))),
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  onGroupingModeChange: PropTypes.func,
  onSelected: PropTypes.func.isRequired,
  selectedItems: PropTypes.array,
  showAllItemsMenu: PropTypes.bool,
};

export { GROUPING_MODE, MANUAL_INSTITUTION_LABEL };

export default AccountMultiSelect;
